import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AIReportFeedbackItem {
  id: number;
  feedbackType: 'super_like' | 'like' | 'dislike' | 'flag';
  userId: number;
  userEmail: string;
  createdAt: string;
}

export interface AdminAIReportFeedbackDetailPageProps {
  aiReport: {
    id: number;
    content: string;
    generatedAt: string;
    report: {
      userEmail: string;
      periodStart: string;
      periodEnd: string;
      periodType: string;
    };
  };
  feedbacks: AIReportFeedbackItem[];

  // URLs
  feedbacksPath: string;
  userPath: (id: number) => string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    reportFor: string;
    // Summary
    feedbackSummaryTitle: string;
    totalFeedbackLabel: string;
    generatedLabel: string;
    breakdownLabel: string;
    noFeedbackYet: string;
    // Feedback types
    superLikeLabel: string;
    likeLabel: string;
    dislikeLabel: string;
    flagLabel: string;
    // Report content
    reportContentTitle: string;
    // User feedback
    userFeedbackTitle: string;
    userColumn: string;
    feedbackTypeColumn: string;
    submittedColumn: string;
    agoLabel: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPeriodDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatPeriodDateWithYear(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// Helper Components
// ============================================================================

function FeedbackTypeBadge({
  type,
  count,
  labels,
}: {
  type: AIReportFeedbackItem['feedbackType'];
  count: number;
  labels: AdminAIReportFeedbackDetailPageProps['labels'];
}) {
  const configs = {
    super_like: { bg: 'bg-success', icon: 'hand-thumbs-up-fill', label: labels.superLikeLabel },
    like: { bg: 'bg-primary', icon: 'hand-thumbs-up', label: labels.likeLabel },
    dislike: { bg: 'bg-danger', icon: 'hand-thumbs-down', label: labels.dislikeLabel },
    flag: { bg: 'bg-warning text-dark', icon: 'flag', label: labels.flagLabel },
  };

  const config = configs[type];

  return (
    <span className={`badge ${config.bg}`}>
      {type === 'super_like' ? (
        <>
          <i className={`bi bi-${config.icon}`} />
          <i className={`bi bi-${config.icon}`} />
        </>
      ) : (
        <i className={`bi bi-${config.icon}`} />
      )}{' '}
      {count} {config.label}
      {count > 1 ? 's' : ''}
    </span>
  );
}

function FeedbackBadge({
  type,
  labels,
}: {
  type: AIReportFeedbackItem['feedbackType'];
  labels: AdminAIReportFeedbackDetailPageProps['labels'];
}) {
  const configs = {
    super_like: { bg: 'bg-success', icon: 'hand-thumbs-up-fill', label: `Super ${labels.likeLabel}` },
    like: { bg: 'bg-primary', icon: 'hand-thumbs-up', label: labels.likeLabel },
    dislike: { bg: 'bg-danger', icon: 'hand-thumbs-down', label: labels.dislikeLabel },
    flag: { bg: 'bg-warning text-dark', icon: 'flag', label: labels.flagLabel },
  };

  const config = configs[type];

  return (
    <span className={`badge ${config.bg}`}>
      {type === 'super_like' ? (
        <>
          <i className={`bi bi-${config.icon}`} />
          <i className={`bi bi-${config.icon}`} />
        </>
      ) : (
        <i className={`bi bi-${config.icon}`} />
      )}{' '}
      {config.label}
    </span>
  );
}

// Simple markdown rendering for AI report content
function MarkdownContent({ content }: { content: string }) {
  // Basic markdown to HTML conversion
  const html = content
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />');

  return (
    <div
      className="ai-report-content"
      dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminAIReportFeedbackDetailPage({
  aiReport,
  feedbacks,
  feedbacksPath,
  userPath,
  labels,
}: AdminAIReportFeedbackDetailPageProps) {
  const feedbackCounts = {
    super_like: feedbacks.filter((f) => f.feedbackType === 'super_like').length,
    like: feedbacks.filter((f) => f.feedbackType === 'like').length,
    dislike: feedbacks.filter((f) => f.feedbackType === 'dislike').length,
    flag: feedbacks.filter((f) => f.feedbackType === 'flag').length,
  };

  return (
    <div>
      <div className="mb-4">
        <a href={feedbacksPath} className="btn btn-outline-secondary mb-3">
          <i className="bi bi-arrow-left" /> {labels.backLabel}
        </a>

        <h1 className="h2">
          <i className="bi bi-stars me-2" />
          {labels.title}
        </h1>
        <p className="text-muted">
          {labels.reportFor} {aiReport.report.userEmail} ·{' '}
          {formatPeriodDate(aiReport.report.periodStart)} -{' '}
          {formatPeriodDateWithYear(aiReport.report.periodEnd)} (
          {aiReport.report.periodType.charAt(0).toUpperCase() + aiReport.report.periodType.slice(1)}
          )
        </p>
      </div>

      {/* Feedback Summary */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-chat-square-heart me-2" />
            {labels.feedbackSummaryTitle}
          </h5>
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>{labels.totalFeedbackLabel}:</strong> {feedbacks.length}
              </p>
              <p>
                <strong>{labels.generatedLabel}:</strong> {formatDateTime(aiReport.generatedAt)}
              </p>
            </div>
            <div className="col-md-6">
              {feedbacks.length > 0 ? (
                <>
                  <div className="mb-2">
                    <strong>{labels.breakdownLabel}:</strong>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {feedbackCounts.super_like > 0 && (
                      <FeedbackTypeBadge
                        type="super_like"
                        count={feedbackCounts.super_like}
                        labels={labels}
                      />
                    )}
                    {feedbackCounts.like > 0 && (
                      <FeedbackTypeBadge type="like" count={feedbackCounts.like} labels={labels} />
                    )}
                    {feedbackCounts.dislike > 0 && (
                      <FeedbackTypeBadge
                        type="dislike"
                        count={feedbackCounts.dislike}
                        labels={labels}
                      />
                    )}
                    {feedbackCounts.flag > 0 && (
                      <FeedbackTypeBadge type="flag" count={feedbackCounts.flag} labels={labels} />
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted">{labels.noFeedbackYet}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Report Content */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-file-text me-2" />
            {labels.reportContentTitle}
          </h5>
        </div>
        <div className="card-body">
          <MarkdownContent content={aiReport.content} />
        </div>
      </div>

      {/* Individual Feedback Entries */}
      {feedbacks.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              <i className="bi bi-people me-2" />
              {labels.userFeedbackTitle}
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{labels.userColumn}</th>
                    <th>{labels.feedbackTypeColumn}</th>
                    <th>{labels.submittedColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((feedback) => (
                    <tr key={feedback.id}>
                      <td>
                        <a href={userPath(feedback.userId)} className="text-decoration-none">
                          <strong>{feedback.userEmail}</strong>
                        </a>
                      </td>
                      <td>
                        <FeedbackBadge type={feedback.feedbackType} labels={labels} />
                      </td>
                      <td>
                        {formatTimeAgo(feedback.createdAt)} {labels.agoLabel}
                        <br />
                        <small className="text-muted">{formatDateTime(feedback.createdAt)}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ai-report-content {
          font-size: 1rem;
          line-height: 1.7;
          color: #2d3748;
        }

        .ai-report-content h1,
        .ai-report-content h2,
        .ai-report-content h3 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }

        .ai-report-content h1 {
          font-size: 1.75rem;
          color: #1a202c;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 0.5rem;
        }

        .ai-report-content h2 {
          font-size: 1.5rem;
          color: #2d3748;
        }

        .ai-report-content h3 {
          font-size: 1.25rem;
          color: #4a5568;
        }

        .ai-report-content ul,
        .ai-report-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .ai-report-content li {
          margin-bottom: 0.5rem;
        }

        .ai-report-content p {
          margin-bottom: 1rem;
        }

        .ai-report-content strong {
          font-weight: 600;
          color: #1a202c;
        }

        .ai-report-content blockquote {
          border-left: 4px solid #667eea;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #4a5568;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}

export default AdminAIReportFeedbackDetailPage;
